<template>
  <div
    class="v-row px-2 my-4 py-2 user-review position-relative"
    itemprop="review"
    itemscope
    itemtype="https://schema.org/Review"
    @mouseenter="toggleTools"
    @mouseleave="toggleTools"
  >
    <MultiTools
      v-if="customerIsAdmin"
      :class="{ show: displayTools }"
      class="slide-in"
    >
      <Tool :type="toolTypes.EDIT_REVIEW" :id="review.review_id_hash" />
      <Tool :type="toolTypes.VIEW_CUSTOMER" :id="review.customer.id_hash" />
    </MultiTools>
    <ReviewHeader
      :title="review.customer.screen_name"
      :created-at="review.created_at"
      :img-src="review.customer.img_src"
      :score="review.score"
      :disabled="review.is_deleted"
      :include-props="true"
    >
      <template #details>
        <CustomerChunk :customer-hash="review.customer.id_hash" class="pb-3" />
      </template>
    </ReviewHeader>
    <div
      v-if="review.title"
      class="quote v-col-12 v-col-lg-10 mt-3 pa-0 styled-text"
    >
      {{ review.title }}
    </div>
    <div
      itemprop="description"
      class="quote v-col-12 v-col-lg-10 mt-3 pa-0 styled-text"
    >
      {{ review.text }}
    </div>
    <div
      v-if="review.text"
      class="v-col-12 v-col-lg-10 l-height-25 l-spacing-half my-3 pa-0"
    >
      <div
        v-if="review.is_reviewable && !alreadyVoted"
        class="d-flex align-center"
      >
        <span class="c-gray">
          {{
            translate('pdp_customer_review_list_item_was_this_review_helpful')
          }}
        </span>
        <span
          v-if="!isSending"
          class="vote-like ml-3 cursor-pointer d-flex"
          @click="sendVote(1)"
        >
          <SvgWrapper class="vote-icon" use="icon_svg_like" dimensions="22" />
        </span>
        <span
          v-if="!isSending"
          class="vote-dislike ml-3 cursor-pointer d-flex"
          @click="sendVote(0)"
        >
          <SvgWrapper
            class="vote-icon"
            use="icon_svg_dislike"
            dimensions="22"
          />
        </span>
        <LoadingOverlay
          v-if="isSending"
          class="position-relative d-inline-flex ml-3"
          width="20"
          height="20"
        />
      </div>
      <div v-else-if="review.is_reviewable" class="d-flex align-center">
        <span class="align-middle c-gray">
          {{
            translate('pdp_customer_review_list_item_thank_you_for_your_rating')
          }}
        </span>
        <SvgWrapper
          use="icon_svg_like"
          class="f-winered ml-3"
          dimensions="22"
          v-if="customerVote === 1"
        />
        <SvgWrapper
          use="icon_svg_dislike"
          class="f-winered ml-3"
          dimensions="22"
          v-if="customerVote === 0"
        />
      </div>
    </div>
    <VTooltip v-if="review.helpful_votes_count" left>
      <template v-slot:activator="{ props }">
        <div class="position-absolute align-end useful-votes" v-bind="props">
          <span>{{ review.helpful_votes_count }}x</span>
          <SvgWrapper
            class="f-graphit ml-2"
            use="icon_svg_like"
            dimensions="22"
          />
        </div>
      </template>
      {{ usefulText }}
    </VTooltip>
  </div>
</template>

<script>
import { useCustomerStore } from '@/stores/customer'
import { mapState } from 'pinia'
import { defineAsyncComponent } from 'vue'
import ReviewHeader from './ReviewHeader.vue'

const SvgWrapper = defineAsyncComponent(
  () => import('@/components/basic/atoms/SvgWrapper.vue')
)
const CustomerChunk = defineAsyncComponent(
  () => import('@/components/pdp/atoms/CustomerChunk.vue')
)
const LoadingOverlay = defineAsyncComponent(
  () => import('@/components/basic/atoms/LoadingOverlay.vue')
)
const MultiTools = defineAsyncComponent(
  () => import('@/components/basic/molecules/MultiTools.vue')
)
const Tool = defineAsyncComponent(
  () => import('@/components/basic/atoms/Tool.vue')
)

const _getModules = (() => {
  let cache

  return async () => {
    if (cache) return cache

    const [{ default: ApiController }, { default: MessageHelper }] =
      await Promise.all([
        await import('@/services/ApiController.js'),
        await import('@/services/helpers/MessageHelper'),
        await import('@/constants/AdminTool')
      ])
    cache = {
      ApiController,
      MessageHelper
    }
    return cache
  }
})()

export default {
  components: {
    ReviewHeader,
    LoadingOverlay,
    Tool,
    MultiTools,
    CustomerChunk,
    SvgWrapper
  },
  props: {
    review: Object,
    toolTypes: Object
  },
  data() {
    return {
      answered: false,
      displayTools: false,
      customerVote: null,
      helpfulVotes: 0,
      customerChunk: null,
      isSending: false
    }
  },
  computed: {
    ...mapState(useCustomerStore, ['customerIsAdmin']),
    alreadyVoted() {
      return this.answered || this.review.voted_by_current_customer
    },
    usefulText() {
      const key =
        this.review.helpful_votes_count > 1
          ? 'pdp_customer_review_list_item_x_found_this_review_helpful_plural'
          : 'pdp_customer_review_list_item_x_found_this_review_helpful_singular'

      return this.translate(key, this.review.helpful_votes_count)
    }
  },
  methods: {
    async sendVote(vote) {
      this.isSending = true
      const { ApiController, MessageHelper } = await _getModules()

      try {
        await ApiController.sendReviewVote(this.review.review_id_hash, vote)
        this.answered = true
        this.customerVote = vote
        this.helpfulVotes += vote
      } catch (e) {
        console.error('Sending review vote failed:', e)
        await MessageHelper.addMessage(
          'pdp_product_rating_save_failure_message'
        )
      }
      this.isSending = false
    },
    toggleTools() {
      this.displayTools = !this.displayTools
    }
  },
  created() {
    this.helpfulVotes = this.review.helpful_votes_count
    this.customerVote = this.review.current_customer_vote
  }
}
</script>

<style scoped>
.vote-icon {
  fill: rgb(var(--v-theme-gray));
  transition: fill var(--transition-duration-l) var(--transition-timing-ease);

  &:hover {
    fill: rgb(var(--v-theme-graphit));
  }
}

.user-review:not(:last-child) {
  border-bottom: 2px solid rgb(var(--v-theme-light-beige));
}

.rate-review:hover {
  fill: rgb(var(--v-theme-graphit));
}

.useful-votes {
  display: flex;
  top: 0;
  right: 0;
}

.admin-tools {
  position: absolute;
  top: 30px;
  right: 0;
}
</style>
